import { midpoint, Point, toBarePngBase64 } from './signatureHelper';

/**
 * The drawing on the evaluation's body diagram.
 *
 * <p>**Strokes are kept in the artwork's own pixels, not the screen's.** The outline is
 * 800x1013, and every point here is a position on that image, whatever size it happened to
 * be drawn at. So the dialog, the thumbnail and the export all redraw the same data at
 * their own scale, and what reaches the API lies exactly over the server's copy of the
 * outline -- the backend composes the two pixel for pixel (`BodyDiagramImage`) and refuses
 * any other size.
 *
 * <p>**These two numbers are a contract with the backend.** They are the size of
 * `public/images/body-outline.png` here and of `documents/body-outline.png` there, and
 * `BodyDiagramImageTest` pins the backend copy. New artwork at another size means changing
 * all of them together.
 *
 * <p>What is exported is ink only, on a transparent canvas: the figure in the PDF is always
 * the server's own artwork, never something the browser sent.
 */
export const BODY_DIAGRAM_WIDTH = 800;
export const BODY_DIAGRAM_HEIGHT = 1013;

export const BODY_DIAGRAM_OUTLINE_SRC = '/images/body-outline.png';

/**
 * A clinical red, deliberately not a brand token -- the signature's `INK` exception, for the
 * same reason: it prints on a white page over a grey outline, and a clinic's colour must not
 * change what a mark means.
 */
export const BODY_DIAGRAM_INK = '#D32F2F';

/** In artwork pixels: about 0.4mm at the 68mm the PDF prints the diagram at. */
export const BODY_DIAGRAM_STROKE_WIDTH = 5;

/** One touch of the pen, from the moment it lands to the moment it lifts. */
export type Stroke = Point[];

/** Keeps a point inside the artwork, so a stroke dragged off the edge ends at the edge. */
export const clampToDiagram = (point: Point): Point => ({
    x: Math.min(Math.max(point.x, 0), BODY_DIAGRAM_WIDTH),
    y: Math.min(Math.max(point.y, 0), BODY_DIAGRAM_HEIGHT),
});

/** The pen settings, at whatever multiple of the artwork's size the canvas is. */
const prepare = (context: CanvasRenderingContext2D, scale: number): void => {
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = BODY_DIAGRAM_INK;
    context.lineWidth = BODY_DIAGRAM_STROKE_WIDTH * scale;
};

/**
 * Draws one stroke, smoothed the way the signature pad smooths: quadratic curves between
 * consecutive midpoints, with the sampled point between them as the control, closed with a
 * straight line to where the pointer actually stopped. A single point is a dot.
 */
const drawStroke = (context: CanvasRenderingContext2D, stroke: Stroke, scale: number): void => {
    if (stroke.length === 0) {
        return;
    }

    const at = (point: Point): Point => ({ x: point.x * scale, y: point.y * scale });
    const first: Point = at(stroke[0]);

    context.beginPath();
    context.moveTo(first.x, first.y);

    if (stroke.length === 1) {
        context.lineTo(first.x, first.y);
        context.stroke();
        return;
    }

    // The same segments, in the same order, that the dialog draws live as the pen moves:
    // straight to the first midpoint, then a curve through each sampled point to the next
    // midpoint. Anything else and the stroke visibly shifts the moment the pen lifts and the
    // picture is redrawn from here.
    const opening: Point = at(midpoint(stroke[0], stroke[1]));

    context.lineTo(opening.x, opening.y);

    for (let i = 1; i < stroke.length - 1; i++) {
        const control: Point = at(stroke[i]);
        const end: Point = at(midpoint(stroke[i], stroke[i + 1]));

        context.quadraticCurveTo(control.x, control.y, end.x, end.y);
    }

    const last: Point = at(stroke[stroke.length - 1]);

    context.lineTo(last.x, last.y);
    context.stroke();
};

/**
 * Clears the canvas and draws every stroke on it, scaled from artwork pixels to the canvas's
 * own. Everything that shows the drawing goes through here, so the dialog, the thumbnail and
 * the exported PNG cannot disagree about what was drawn.
 */
export const drawStrokes = (canvas: HTMLCanvasElement, strokes: Stroke[]): void => {
    const context: CanvasRenderingContext2D | null = canvas.getContext('2d');

    // Null under jsdom, which has no canvas backend.
    if (!context) {
        return;
    }

    const scale: number = canvas.width / BODY_DIAGRAM_WIDTH;

    context.clearRect(0, 0, canvas.width, canvas.height);
    prepare(context, scale);
    strokes.forEach((stroke) => drawStroke(context, stroke, scale));
};

/**
 * What the API takes: base64 of a transparent 800x1013 PNG holding the ink alone, with no
 * `data:` prefix. Empty when nothing was drawn, which the request mapper drops -- a sheet
 * nobody drew on sends no field at all rather than a blank picture.
 *
 * Also empty where there is no canvas to draw with (jsdom), for the same reason the
 * signature pad swallows `toDataURL` there: nothing to export and nothing to report.
 */
export const exportBodyDiagram = (strokes: Stroke[]): string => {
    if (strokes.length === 0) {
        return '';
    }

    const canvas: HTMLCanvasElement = document.createElement('canvas');

    canvas.width = BODY_DIAGRAM_WIDTH;
    canvas.height = BODY_DIAGRAM_HEIGHT;

    if (!canvas.getContext('2d')) {
        return '';
    }

    drawStrokes(canvas, strokes);

    try {
        return toBarePngBase64(canvas.toDataURL('image/png'));
    } catch {
        return '';
    }
};
